/**
 * The colours of a utilisation band. The server classifies every figure and
 * sends its band (`unassigned`, `under`, `optimal`, `over`) with the edges it
 * used; this module maps a band to the soft `bars[N]` fill and the label
 * colour beside it, so the Planning Board, the Dashboard and Reports read as
 * one family. It holds no edge and classifies no number.
 */

import type { Theme } from '@mui/material';
import type { BarIndex } from '../theme/tokens';

export type UtilisationBand = 'unassigned' | 'under' | 'optimal' | 'over';

/**
 * The healthy band's edges as the API sends them beside every figure it
 * classifies (`bands` on the People endpoints). Copy that names the band is
 * built from these, so the screen states the range the server classified by.
 */
export interface UtilisationBandEdges {
  under: number;
  over: number;
}

/** "80–110%", from the server's edges. */
export const bandRangeLabel = (edges: UtilisationBandEdges): string =>
  `${edges.under}–${edges.over}%`;

/** Counts of people (or weeks) per band, as the server counts them. */
export type UtilisationBandCounts = Record<UtilisationBand, number>;

/**
 * Canonical band → soft `bars[N]` fill mapping (the prototype `utilTone`
 * palette): unassigned = pale blue, under = pale mint, optimal = pale moss,
 * over = pale rose. This is the single source for the **fill** channel so the
 * Planning Board heatmap, Dashboard utilisation list and Reports bars can't
 * drift apart (the satellite tone modules delegate here).
 */
export const UTIL_BAND_BAR_INDEX: Record<UtilisationBand, BarIndex> = {
  unassigned: 1,
  under: 2,
  optimal: 5,
  over: 6,
};

/** Soft pastel `bars[N]` fill for a utilisation band (mode-aware via theme). */
export const getUtilisationBandFill = (band: UtilisationBand, theme: Theme): string =>
  theme.palette.bars[UTIL_BAND_BAR_INDEX[band]].fill;

/**
 * Saturated numeric-label colour for a utilisation band — under/unassigned read
 * as a muted grey (not alarming), optimal as green, over as red. Mode-aware: the
 * deeper `.dark` shade in light mode (readable on pale surfaces), `.main` in
 * dark mode. The single source for the Reports bars, the Planning Board heatmap
 * and any other "82%" label sitting beside a soft fill.
 */
export const getUtilisationBandTextColour = (band: UtilisationBand, theme: Theme): string => {
  const light = theme.palette.mode === 'light';
  switch (band) {
    case 'optimal':
      return light ? theme.palette.success.dark : theme.palette.success.main;
    case 'over':
      return light ? theme.palette.error.dark : theme.palette.error.main;
    case 'under':
    case 'unassigned':
    default:
      return theme.palette.text.secondary;
  }
};
